import { ThemeEvents } from '@theme/events';

class PackPicker extends HTMLElement {
  connectedCallback() {
    this.initialize();
  }

  initialize(reset = false) {
    this.disconnectedCallback();
    this.busy = false;
    this.sectionTarget = this.closest('.shopify-section');
    this.readConfiguration();
    if (reset || !this.selections) {
      this.selections = new Map((this.configuration.offers || []).map(offer => [offer.key, { ...offer.selection }]));
      this.paidQuantities = new Map((this.configuration.offers || []).filter(offer => offer.adjustable).map(offer => [offer.key, offer.totalGames]));
      for (const input of this.querySelectorAll('[data-pack-option]')) input.checked = input.value === this.configuration.defaultOffer;
    }
    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleMixClick);
    this.sectionTarget?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.syncSelection();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('click', this.handleMixClick);
    this.sectionTarget?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  readConfiguration() {
    try {
      this.configuration = JSON.parse(this.querySelector('[data-offer-config]')?.textContent || '{}');
      this.configuration.offers = [...this.querySelectorAll('[data-bundle-tier-config]')].map(node => JSON.parse(node.textContent));
      this.configuration.defaultOffer = this.configuration.offers.find(offer => offer.defaultSelected)?.key || this.configuration.offers[0]?.key;
      for (const variant of this.configuration.variants || []) variant.maxQuantity ??= Number.MAX_SAFE_INTEGER;
    }
    catch (_) { this.configuration = {}; }
  }

  handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-pack-option]') || this.busy) return;
    this.setError('');
    this.syncSelection();
  };

  handleMixClick = (event) => {
    const quantityButton = event.target instanceof Element ? event.target.closest('[data-paid-quantity-change]') : null;
    if (quantityButton instanceof HTMLButtonElement) {
      if (!quantityButton.disabled && !this.busy) this.changePaidQuantity(quantityButton.dataset.paidQuantityChange, Number(quantityButton.dataset.delta));
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('[data-pack-change]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled || this.busy) return;
    const key = button.closest('[data-pack-panel]')?.dataset.packPanel;
    const edition = button.dataset.packChange;
    const selection = this.selections.get(key);
    const variant = this.configuration.variants?.find(v => v.edition === edition);
    if (selection && edition && variant) {
      const current = Number(selection[edition] || 0);
      const delta = Number(button.dataset.delta);
      const next = delta > 0 ? (current === 0 ? variant.minQuantity : current + variant.increment) : (current <= variant.minQuantity ? 0 : current - variant.increment);
      this.setQuantity(key, edition, next);
    }
  };

  paidQuantityLimit(offer) {
    const nextPromotion = Math.min(...(this.configuration.offers || []).filter(tier => tier.freeQuantity > 0).map(tier => tier.totalGames));
    const stock = (this.configuration.variants || []).filter(variant => variant.available && variant.eligible).reduce((total, variant) => total + variant.maxQuantity, 0);
    return Math.max(1, Math.min(offer.quantityOptions?.length || 1, nextPromotion - 1, stock));
  }

  effectiveOffer(offer) {
    if (!offer?.adjustable) return offer;
    const quantity = this.paidQuantities?.get(offer.key) || offer.totalGames;
    const option = offer.quantityOptions?.find(option => option.quantity === quantity);
    return option ? { ...offer, totalGames: quantity, paidQuantity: quantity, price: option.price, separateValue: option.price, priceLabel: option.formattedPrice, cta: option.cta, countLabel: option.countLabel, displayTitle: quantity === 1 ? offer.title : option.heading } : offer;
  }

  changePaidQuantity(key, delta) {
    const base = this.configuration.offers?.find(offer => offer.key === key);
    const offer = this.effectiveOffer(base);
    const selection = this.selections.get(key);
    const next = (offer?.totalGames || 0) + delta;
    if (this.busy || !base?.adjustable || !selection || ![-1, 1].includes(delta) || next < 1 || next > this.paidQuantityLimit(base)) return false;
    const proposed = { ...selection };
    if (delta > 0) {
      const preferred = [...(this.configuration.variants || [])].sort((a, b) => Number(b.edition === this.configuration.edition) - Number(a.edition === this.configuration.edition));
      const variant = preferred.find(variant => variant.available && variant.eligible && variant.variantId && variant.increment === 1 && (proposed[variant.edition] || 0) < variant.maxQuantity && ((proposed[variant.edition] || 0) > 0 || variant.minQuantity === 1));
      if (!variant) return false;
      proposed[variant.edition] = (proposed[variant.edition] || 0) + 1;
    } else if (Object.values(proposed).reduce((total, quantity) => total + Number(quantity), 0) > next) {
      const preferred = [...(this.configuration.variants || [])].sort((a, b) => Number(a.edition === this.configuration.edition) - Number(b.edition === this.configuration.edition));
      const variant = preferred.find(variant => variant.increment === 1 && (proposed[variant.edition] || 0) > (variant.edition === this.configuration.edition ? Math.max(1, variant.minQuantity) : 0) && ((proposed[variant.edition] || 0) - 1 === 0 || (proposed[variant.edition] || 0) - 1 >= variant.minQuantity));
      if (!variant) return false;
      proposed[variant.edition] -= 1;
    }
    this.paidQuantities.set(key, next);
    this.selections.set(key, proposed);
    this.setError('');
    this.syncSelection();
    return true;
  }

  setQuantity(key, edition, quantity) {
    const base = this.configuration.offers?.find(o => o.key === key);
    const offer = this.effectiveOffer(base);
    const variant = this.configuration.variants?.find(v => v.edition === edition);
    const selection = this.selections.get(key);
    if (this.busy || !offer || !selection || !variant) return false;
    if (!Number.isInteger(quantity) || quantity < 0) {
      this.setError(this.dataset.negativeLabel);
      return false;
    }
    if (edition === this.configuration.edition && quantity < 1) {
      this.setError(this.dataset.mainLabel);
      return false;
    }
    const proposed = { ...selection, [edition]: quantity };
    const count = Object.values(proposed).reduce((sum, q) => sum + Number(q), 0);
    if (count > (offer.adjustable ? this.paidQuantityLimit(base) : offer.totalGames)) {
      this.setError(this.dataset.limitLabel);
      return false;
    }
    if (quantity > 0 && (!variant.available || !variant.eligible || !variant.variantId || quantity > variant.maxQuantity || quantity < variant.minQuantity || quantity % variant.increment !== 0)) {
      this.setError(`${variant.label}: ${this.dataset.unavailableLabel}`);
      return false;
    }
    if (offer.adjustable) this.paidQuantities.set(key, count);
    this.selections.set(key, proposed);
    this.setError('');
    this.syncSelection();
    return true;
  }

  get selectedInput() { return this.querySelector('[data-pack-option]:checked'); }
  get selectedOffer() { return this.effectiveOffer(this.configuration.offers?.find(offer => offer.key === this.selectedInput?.value)); }
  get productForm() { return this.closest('product-form-component'); }

  selectionStatus(offer) {
    const selection = this.selections?.get(offer?.key);
    if (!offer || !selection) return { valid: false, count: 0, reason: this.dataset.unavailableLabel };
    const count = Object.values(selection).reduce((sum, quantity) => sum + Number(quantity), 0);
    let valid = count === offer.totalGames;
    let reason = '';
    for (const [edition, quantity] of Object.entries(selection)) {
      const variant = this.configuration.variants?.find(v => v.edition === edition);
      if (!Number.isInteger(quantity) || quantity < 0 || quantity > 0 && (!variant?.available || !variant?.eligible || !variant?.variantId || quantity > variant.maxQuantity || quantity < variant.minQuantity || quantity % variant.increment !== 0)) {
        valid = false;
        reason = `${variant?.label || edition}: ${this.dataset.unavailableLabel}`;
      }
    }
    if (Number(selection[this.configuration.edition] || 0) < 1) {
      valid = false;
      reason = this.dataset.mainLabel;
    }
    return { valid, count, reason };
  }

  formatLabel(label, count, total) {
    return (label || '').replaceAll('__TL_COUNT__', String(count)).replaceAll('__TL_TOTAL__', String(total));
  }

  syncOffer(base) {
    const offer = this.effectiveOffer(base);
    const selection = this.selections.get(offer.key);
    const card = this.querySelector(`[data-offer-card="${offer.key}"]`);
    if (!selection || !card) return;
    if (offer.adjustable) {
      const input = card.querySelector('[data-pack-option]');
      if (input) Object.assign(input.dataset, { cta: offer.cta, price: offer.priceLabel, totalGames: String(offer.totalGames) });
      const title = card.querySelector('.tl-offer-card__title');
      if (title) title.textContent = offer.displayTitle;
      const count = card.querySelector('.tl-offer-card__count');
      if (count) count.textContent = offer.countLabel;
      const price = card.querySelector('.tl-offer-card__total');
      if (price) price.textContent = offer.priceLabel;
      const tagline = card.querySelector('[data-tier-tagline]');
      if (tagline) tagline.hidden = offer.totalGames > 1;
      const controls = this.querySelector(`[data-paid-quantity-panel="${offer.key}"]`);
      if (controls) {
        controls.hidden = offer.key !== this.selectedOffer?.key;
        controls.querySelector('[data-paid-quantity-value]').textContent = String(offer.totalGames);
        controls.querySelector('[data-delta="-1"]').disabled = this.busy || offer.totalGames <= 1;
        controls.querySelector('[data-delta="1"]').disabled = this.busy || offer.totalGames >= this.paidQuantityLimit(base);
        controls.querySelector('[data-paid-quantity-hint]').hidden = offer.totalGames < this.paidQuantityLimit(base);
      }
    }
    const ordered = [...(this.configuration.variants || [])];
    const contents = ordered.filter(v => selection[v.edition] > 0).map(v => `${selection[v.edition]} ${v.label}`).join(' · ');
    const images = card.querySelector('.tl-offer-card__images');
    if (images) {
      images.classList.toggle('tl-offer-card__images--1', offer.totalGames === 1);
      const imageOrder = ordered.filter(v => v.edition !== this.configuration.edition).concat(ordered.filter(v => v.edition === this.configuration.edition));
      const fragments = document.createDocumentFragment();
      for (const variant of imageOrder) {
        const template = this.querySelector(`[data-box-template="${variant.edition}"]`);
        if (!(template instanceof HTMLTemplateElement)) continue;
        for (let i = 0; i < (selection[variant.edition] || 0); i++) {
          const copy = template.content.cloneNode(true);
          if (variant.edition === this.configuration.edition) copy.querySelector('.tl-offer-card__box')?.classList.add('tl-offer-card__box--current');
          fragments.append(copy);
        }
      }
      images.style.setProperty('--tl-box-count', String(Math.max(1, Object.values(selection).reduce((sum, quantity) => sum + quantity, 0))));
      images.replaceChildren(fragments);
      images.setAttribute('aria-label', contents);
    }
    const panel = this.querySelector(`[data-pack-panel="${offer.key}"]`);
    if (!panel) return;
    panel.hidden = offer.key !== this.selectedOffer?.key;
    const heading = panel.querySelector('[data-mix-heading]');
    if (heading) heading.textContent = offer.adjustable ? this.dataset.paidChooseLabel : this.formatLabel(this.dataset.chooseLabel, offer.totalGames, offer.totalGames);
    const count = this.selectionStatus(offer).count;
    const counter = panel.querySelector('[data-mix-count]');
    if (counter) counter.textContent = offer.adjustable ? offer.countLabel : this.formatLabel(this.dataset.selectionLabel, count, offer.totalGames);
    const freeSlots = Math.max(0, offer.totalGames - count);
    const capacity = offer.adjustable ? Math.max(0, this.paidQuantityLimit(base) - count) : freeSlots;
    const helper = panel.querySelector('[data-mix-help]');
    if (helper) helper.textContent = offer.adjustable ? this.dataset.paidMixLabel : this.formatLabel(freeSlots ? this.dataset.allocationLabel : this.dataset.completeLabel, freeSlots || count, offer.totalGames);
    for (const row of panel.querySelectorAll('[data-mix-row]')) {
      const edition = row.dataset.mixRow;
      const variant = this.configuration.variants?.find(v => v.edition === edition);
      const quantity = Number(selection[edition] || 0);
      const value = row.querySelector('[data-pack-quantity]');
      if (value) value.textContent = String(quantity);
      const minus = row.querySelector('[data-delta="-1"]');
      const plus = row.querySelector('[data-delta="1"]');
      if (minus) minus.disabled = this.busy || quantity === 0 || edition === this.configuration.edition && quantity <= Math.max(1, variant?.minQuantity || 1);
      if (plus) plus.disabled = this.busy || !variant?.available || !variant?.eligible || !variant?.variantId || capacity < (quantity === 0 ? variant.minQuantity : variant.increment) || quantity >= variant.maxQuantity;
      const availability = row.querySelector('[data-mix-availability]');
      if (availability) availability.textContent = !variant?.available || !variant?.eligible || !variant?.variantId ? this.dataset.unavailableLabel : '';
    }
  }

  syncSelection() {
    for (const offer of this.configuration.offers || []) this.syncOffer(offer);
    const selected = this.selectedInput;
    const offer = this.selectedOffer;
    const status = this.selectionStatus(offer);
    const enabled = offer?.enabled === true && status.valid;
    const cta = this.busy ? this.dataset.addingLabel : selected?.dataset.cta || '';
    const sticky = this.sectionTarget?.querySelector('sticky-add-to-cart');
    for (const button of [this.productForm?.querySelector('[ref="addToCartButton"]'), sticky?.querySelector('[ref="addToCartButton"]')]) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = this.busy || !enabled;
      button.setAttribute('aria-label', cta);
      button.setAttribute('aria-busy', String(this.busy));
      const label = button.querySelector('[data-add-to-cart-label], [data-sticky-add-to-cart-label]');
      if (label) label.textContent = cta;
    }
    if (sticky instanceof HTMLElement) {
      sticky.dataset.offerEnabled = String(enabled);
      const price = sticky.querySelector('[data-testid="sticky-price-display"]');
      if (price) price.textContent = selected?.dataset.price || '';
      const quantity = sticky.querySelector('[ref="quantityDisplay"]');
      if (quantity instanceof HTMLElement) quantity.style.display = 'none';
    }
    const message = this.querySelector('[data-pack-status]');
    if (message) message.textContent = status.reason || (!status.valid ? this.formatLabel(this.dataset.selectionLabel, status.count, offer?.totalGames || 0) : offer?.enabled !== true ? offer?.reason || this.dataset.unavailableLabel : '');
    const property = this.querySelector('[data-bundle-tier-property]');
    if (property instanceof HTMLInputElement) property.value = offer?.title || '';
    this.productForm?.setAttribute('data-pack-selection', selected?.value || '');
    this.dispatchEvent(new CustomEvent('tl:offer-change', { bubbles: true, detail: { offer, selection: this.selections.get(offer?.key), enabled, busy: this.busy } }));
  }

  setBusy(value) {
    this.busy = value;
    this.setAttribute('aria-busy', String(value));
    for (const input of this.querySelectorAll('[data-pack-option]')) {
      if (input instanceof HTMLInputElement) input.disabled = value || input.dataset.selectable !== 'true';
    }
    this.syncSelection();
  }

  setError(message) {
    const error = this.querySelector('[data-pack-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  handleVariantUpdate = (event) => {
    if (event.detail?.data?.productId?.toString() !== this.dataset.productId) return;
    const updated = event.detail.data.html?.querySelector(`pack-picker[data-product-id="${this.dataset.productId}"]`);
    if (updated instanceof HTMLElement) {
      const incoming = updated.querySelector('[data-offer-config]');
      const current = this.querySelector('[data-offer-config]');
      if (incoming && current) current.textContent = incoming.textContent;
      for (const next of updated.querySelectorAll('[data-bundle-tier-config]')) {
        const currentTier = this.querySelector(`[data-tier-key="${next.dataset.tierKey}"]`);
        if (currentTier) currentTier.textContent = next.textContent;
      }
      this.readConfiguration();
      for (const input of this.querySelectorAll('[data-pack-option]')) {
        const next = updated.querySelector(`[data-pack-option][value="${input.value}"]`);
        if (input instanceof HTMLInputElement && next instanceof HTMLInputElement) {
          Object.assign(input.dataset, next.dataset);
          input.disabled = this.busy || next.disabled;
        }
      }
    }
    queueMicrotask(() => this.syncSelection());
  };

  get selectedItems() {
    if (this.busy) return null;
    const offer = this.selectedOffer;
    const status = this.selectionStatus(offer);
    if (!offer || offer.enabled !== true || !status.valid) {
      this.setError(status.reason || (status.valid ? offer?.reason : this.formatLabel(this.dataset.selectionLabel, status.count, offer?.totalGames || 0)) || this.dataset.errorLabel);
      return null;
    }
    const selection = this.selections.get(offer.key);
    return this.configuration.variants.filter(v => selection[v.edition] > 0).map(v => ({ variantId: String(v.variantId), quantity: selection[v.edition], properties: { _bundle_tier: offer.title } }));
  }
}

if (!customElements.get('pack-picker')) customElements.define('pack-picker', PackPicker);

// Section reloads replace settings and blocks; reset defaults without duplicating handlers.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('pack-picker').forEach(picker => picker.initialize(true));
});
document.addEventListener('shopify:block:select', (event) => {
  const picker = event.target.closest('pack-picker');
  const card = event.target.closest('[data-offer-card]');
  const input = card?.querySelector('[data-pack-option]');
  if (picker && input && !picker.busy && !input.disabled) {
    input.checked = true;
    picker.syncSelection();
  }
});
